import { useMemo } from 'react';
import { includes } from 'lodash';

import {
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_RANKING,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  SHUFFLE_REVERSE,
  SHUFFLE_REVERSE_WITH_LAST_FIXED,
  SHUFFLE_REVERSE_WITH_LAST_TWO_FIXED,
  ANSWER_NONE_OF_THE_ABOVE,
} from '../../../constants';

import {
  getScorepointShuffleType,
  shuffleArray,
  getVisibleRowsAndColumns,
  isMatrixTypeLikert,
} from '../../../utils';

import { shuffleAnswers } from '../../Answers/utils';

import {
  transformMediaQuestionAttributes,
  getAnswersWrapperClassName,
} from '../utils';
import { carryForwardService } from '../../../logic';

function useSetup(question) {
  const { answers, style, type, shuffle: shuffleType } = question;
  const wrapperClassName = getAnswersWrapperClassName(question);

  const mediaAttributes = transformMediaQuestionAttributes({
    ...question,
    questionRefId: question.refId,
  });

  // We should shuffle answers only once for the following question types
  // in order to keep the same order in both QuestionBody and Answers components
  const isNodeTypeSupported = [
    QUESTION_SINGLE,
    QUESTION_MULTIPLE,
    QUESTION_RANKING,
    QUESTION_MATRIX_SINGLE,
    QUESTION_MATRIX_MULTIPLE,
  ].includes(style ?? type);

  // We should shuffle scorepoints only once for the following question types
  // in order to keep the same order in both QuestionBody and Answers components
  const canShuffleScorepoints = isMatrixTypeLikert(question);

  const shuffledScorepoints = useMemo(() => {
    if (!canShuffleScorepoints) {
      return [];
    }

    const transformedScorepoints = carryForwardService.getCarriedForwardScorepoints(
      question,
      Pollfish.store.getRespondentKnowledge(),
      [
        ...Pollfish.survey.props.questions,
        ...Pollfish.screening.getQuestions(),
      ],
    );

    // At the moment, the selected answers are kept in DOM, as indexes
    // and we get the scorePoint id based on the index of the array question.scorepoints
    // But now, every answer can have different order on the scorepoint array,
    // so we override this array, and push every answer's ordering
    // eslint-disable-next-line
    let newScorepoints = [];

    let data;
    answers.forEach((_, idx) => {
      const normalizedShuffleType = getScorepointShuffleType(shuffleType);
      const hasReverseShuffleType = includes(
        [
          SHUFFLE_REVERSE,
          SHUFFLE_REVERSE_WITH_LAST_FIXED,
          SHUFFLE_REVERSE_WITH_LAST_TWO_FIXED,
        ],
        normalizedShuffleType,
      );

      const noneOfTheAboveScorepoint = transformedScorepoints.find(
        (scorepoint) => scorepoint.style === ANSWER_NONE_OF_THE_ABOVE,
      );

      const filteredScorepoints = transformedScorepoints.filter(
        (scorepoint) => scorepoint.style !== ANSWER_NONE_OF_THE_ABOVE,
      );

      // for reverse shuffle, do the shuffling only once so that the
      // order of scorepoints for each answer is the same
      if (!hasReverseShuffleType || (hasReverseShuffleType && idx === 0)) {
        data = shuffleArray(filteredScorepoints, normalizedShuffleType);
        if (noneOfTheAboveScorepoint) {
          data.push(noneOfTheAboveScorepoint);
        }
      }

      // shuffle scorepoints per answer
      newScorepoints.push([...data]);
    });

    return newScorepoints;
  }, [question, answers, canShuffleScorepoints, shuffleType]);

  const shuffledAnswers = useMemo(() => {
    if (!isNodeTypeSupported) {
      return answers;
    }

    return shuffleAnswers(question);
  }, [isNodeTypeSupported, question, answers]);

  const { visibleAnswers, visibleScorepoints } = useMemo(() => {
    if (!isNodeTypeSupported) {
      return {
        visibleAnswers: shuffledAnswers,
        visibleScorepoints: shuffledScorepoints,
      };
    }

    const { rows, columns } = getVisibleRowsAndColumns(
      question,
      shuffledAnswers,
      shuffledScorepoints,
    );

    return {
      visibleAnswers: rows,
      visibleScorepoints: columns,
    };
  }, [shuffledAnswers, shuffledScorepoints, isNodeTypeSupported, question]);

  return {
    visibleAnswers,
    visibleScorepoints,
    mediaAttributes,
    wrapperClassName,
  };
}

export default useSetup;
